/**
 * Utilities for generating openhasp.command popup JSONL parameters from a
 * template page defined in the designer.
 */

import { getObjectSchema } from '../Hasp/properties/v2/wireFormat.js';

/**
 * Serialise a template page (or a subtree within it) into JSONL lines
 * suitable for openhasp.command keyword:jsonl.
 *
 * @param {HaspPage} templatePage  - template page from the designer
 * @param {string}   entity        - HA entity ID injected as tag
 * @param {number}   targetPageId  - runtime page where objects appear
 * @param {number}   [rootObjectId] - haspid of the root object to export;
 *                                    omit to export the full template page
 */
export function generatePopupJsonl(templatePage, entity, targetPageId, rootObjectId) {
  const tp = targetPageId;
  const exportData = [];

  if (rootObjectId != null) {
    const root = _findById(templatePage, rootObjectId);
    if (root) root.export(templatePage, exportData, false);
  } else {
    templatePage.export(null, exportData, false);
    exportData.shift(); // drop page-header entry
  }

  return exportData.map(obj => _buildJsonlLine(obj, tp, entity)).join('\n');
}

/**
 * Build a single JSONL line for one exported object.
 * Numeric Jinja2 expressions (like brightness) must appear unquoted in the
 * JSON so that after HA template evaluation the plate receives an integer
 * (117) rather than a string ("117").  We do this by deleting the field from
 * the object before JSON.stringify, then appending it unquoted at the end.
 */
function _buildJsonlLine(obj, tp, entity) {
  const item = { ...obj };

  item.page = tp;
  delete item.comment;
  // Custom-component ownership is Designer metadata. Popup JSONL is sent to
  // the firmware through openhasp.command, so it must follow the same wire
  // contract as the regular pages.jsonl export while the live model keeps
  // both fields for editing and design saves.
  delete item.custom_component_id;
  delete item.custom_component_name;

  // Collect numeric Jinja2 fields that must be appended unquoted.
  const unquotedFields = {};

  if (entity) {
    item.tag = entity;

    // Widgets that declare templateEntityBinding read that HA attribute at
    // export time to reflect the popup's bound entity — driven entirely by
    // the widget's own v2 schema, never by widget-name branching here.
    const binding = getObjectSchema(item.obj)?.templateEntityBinding;
    if (binding) {
      const expr = _buildTemplateEntityExpression(binding, entity);
      if (binding.numeric) {
        unquotedFields[binding.property] = expr;
        delete item[binding.property];
      } else {
        item[binding.property] = expr;
      }
    }
  }

  let json = JSON.stringify(item);

  // Append numeric template fields unquoted: "val":{{ ... }}
  for (const [key, expr] of Object.entries(unquotedFields)) {
    json = json.replace(/\}$/, `,"${key}":${expr}}`);
  }

  return json;
}

/**
 * Build the Jinja2 expression for a schema-declared templateEntityBinding.
 * `rgb_color` gets the same multi-line hex conversion every RGB-attribute
 * read in this codebase uses (see bindingGenerators.js's generateState);
 * any other attribute is a plain state_attr() read, optionally defaulted
 * and/or coerced to int for numeric properties.
 * @private
 */
function _buildTemplateEntityExpression({ attribute, fallback, numeric }, entity) {
  if (attribute === 'rgb_color') {
    return (
      `{% set rgb = state_attr('${entity}', 'rgb_color') %}` +
      `{{ '#%02x%02x%02x' | format(rgb[0],rgb[1],rgb[2]) if rgb else '#ffffff' }}`
    );
  }

  let expr = `state_attr('${entity}', '${attribute}')`;
  if (fallback !== undefined) expr += ` | default(${fallback})`;
  if (numeric) expr += ` | int`;
  return `{{ ${expr} }}`;
}

function _findById(node, haspid) {
  for (const child of (node.getChildren?.() ?? [])) {
    if (child.haspid === haspid) return child;
    if (typeof child.isContainer === 'function' && child.isContainer()) {
      const found = _findById(child, haspid);
      if (found) return found;
    }
  }
  return null;
}
